import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  SliderComponent,
  SvpEpisodeSliderComponent,
} from '@vgtv/api-client/lib/component/slider';
import type { ListComponent } from '@vgtv/api-client/lib/component/list';
import type { ContinueWatchingComponent } from '@vgtv/api-client/lib/component/continue_watching';
import { Layout } from '@vgtv/api-client/lib/component';
import type {
  ComponentId,
  ComponentType,
  DataType,
} from '@vgtv/api-client/lib/component';
import type { AsyncThunk } from '@reduxjs/toolkit';

import {
  selectIsComponentFetched,
  selectIsComponentFetching,
} from '../../modules/fetching';
import { useViewportType } from '../utils/useViewportType';
import {
  selectComponentHasNextPage,
  selectComponentNextPageParam,
} from '../../modules/components';
import { ViewportType } from '../../utils/viewport/getViewportType';
import { useAppDispatch, useAppSelector } from '../../modules/hooks';
import type { SetRequired } from '../../utils/types';
import { clearObject } from '../../utils/clearObject';
import { SvpFilters } from '../../services/svp/filters';
import { replaceTemplate } from '../../services/svp/template';
import type { Vendor } from '../../types';
import { useVendor } from '../VendorPovider/VendorPovider';

import { getComponentDataFetchLimit } from './getComponentDataFetchLimit';
import { getComponentItemsCount } from './components/getComponentItemsCount';

const AB_SPORT_LIVE_STREAMS_SLIDER_ID = '601138f00f2204001290e37e';

function useComponentDataFetch({
  componentConfig,
  fetchAction,
  fetchLimit,
  assetIds,
}: {
  componentConfig: Pick<
    | SliderComponent
    | ListComponent
    | ContinueWatchingComponent
    | SvpEpisodeSliderComponent,
    'id' | 'type' | 'data' | 'options'
  >;
  fetchAction: AsyncThunk<
    any,
    {
      componentId?: string;
      type?: ComponentType;
      dataType?: DataType;
      dataUrl: string;
      sort?: string;
      filter?: string;
      page?: number;
      limit?: number;
      vendor: Vendor;
    },
    {}
  >;
  fetchLimit?: number;
  assetIds?: number[];
}) {
  const dispatch = useAppDispatch();
  const [hasPreset, setHasPreset] = useState(false);
  const [offset, setOffset] = useState(0);
  const { viewportType } = useViewportType();
  const { vendor } = useVendor();

  const {
    id,
    type,
    data: { type: dataType, url, source },
    options: { layout, useVerticalPosters },
  } = componentConfig;

  const componentLayout = useMemo(
    () =>
      layout === Layout.VERTICAL || useVerticalPosters
        ? Layout.VERTICAL
        : Layout.HORIZONTAL,
    [layout, useVerticalPosters]
  );

  const limit = useMemo(
    () =>
      fetchLimit
        ? fetchLimit
        : getComponentDataFetchLimit({
            componentType: type,
            dataType,
            componentLayout,
            viewportType,
          }),
    [dataType, type, componentLayout, viewportType, fetchLimit]
  );

  const itemsCount = useMemo(
    () =>
      getComponentItemsCount({
        componentType: type,
        componentLayout,
        dataType,
      }),
    [componentLayout, dataType, type]
  );

  const serializedParams = JSON.stringify(source?.params);

  const fetchActionArgs: {
    componentId: ComponentId;
    type:
      | ComponentType.LIST
      | ComponentType.SLIDER
      | ComponentType.CONTINUE_WATCHING;
    dataUrl?: string;
    sort?: string;
    filter?: string;
    limit?: number;
    vendor: Vendor;
  } = useMemo(() => {
    const filters = new SvpFilters();

    if (assetIds?.length) {
      filters.append(`assetId::${assetIds.slice(0, limit).join(',')}`);
    }

    if (source?.params && 'filter' in source.params) {
      Object.entries(source.params.filter || {}).forEach(
        ([property, value]) => {
          if (value === null) {
            return;
          }

          if (property === 'flightTimesStart') {
            filters.append(
              `flightTimes.start<=${replaceTemplate(String(value))}`
            );
          } else {
            filters.append(`${property}::${value}`);
          }
        }
      );
    }

    return clearObject({
      componentId: id,
      type,
      dataType,
      dataUrl: url,
      filter: filters.toString(),
      vendor,
      limit:
        id === AB_SPORT_LIVE_STREAMS_SLIDER_ID &&
        viewportType === ViewportType.MOBILE
          ? 30
          : limit,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    type,
    dataType,
    url,
    viewportType,
    limit,
    assetIds,
    serializedParams,
    vendor,
  ]);

  useEffect(() => {
    if (url) {
      if (fetchAction) {
        setOffset(0);

        dispatch(
          fetchAction(
            fetchActionArgs as SetRequired<typeof fetchActionArgs, 'dataUrl'>
          )
        )
          .unwrap()
          .then(() => {
            setOffset(fetchActionArgs.limit!);
          })
          .catch(console.error);
      }
    } else {
      setHasPreset(true);
    }
  }, [fetchActionArgs, url, dispatch, fetchAction]);

  const nextPageParam = useAppSelector((state) =>
    selectComponentNextPageParam(state, id)
  );
  const hasNextPage = useAppSelector((state) =>
    selectComponentHasNextPage(state, id)
  );

  const fetchNextPage = useCallback(async () => {
    const filters = SvpFilters.from(fetchActionArgs.filter);

    if (assetIds?.length) {
      filters.append(
        `assetId::${assetIds
          .slice(offset, offset + fetchActionArgs.limit!)
          .join(',')}`
      );
    }

    try {
      await dispatch(
        fetchAction({
          ...(fetchActionArgs as SetRequired<
            typeof fetchActionArgs,
            'dataUrl'
          >),
          page:
            nextPageParam && !assetIds?.length
              ? parseInt(nextPageParam, 10)
              : undefined,
          filter: filters.toString(),
        })
      ).unwrap();

      if (assetIds?.length) {
        setOffset(
          offset +
            assetIds.slice(offset, offset + fetchActionArgs.limit!).length
        );
      }
    } catch (err) {
      console.error(err);
    }
  }, [dispatch, fetchAction, fetchActionArgs, nextPageParam, offset, assetIds]);

  const isFetched = useAppSelector((state) =>
    selectIsComponentFetched(state, id)
  );
  const isFetching = useAppSelector((state) =>
    selectIsComponentFetching(state, id)
  );

  return {
    isFetched: hasPreset || isFetched,
    hasNextPage:
      hasNextPage ||
      (Boolean(assetIds?.length) &&
        assetIds!.slice(offset, offset + fetchActionArgs.limit!).length > 0),
    isFetching,
    fetchNextPage,
    visibleItemsCount: itemsCount,
  };
}

export { useComponentDataFetch };
